import { cadenzaSessionStorage } from '@disy/lib/storage';

import type { DisplayState } from 'cadenza/api-client/workbook-api';
import { getStore } from 'cadenza/store/cadenza-store';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import { CommonViewSettingsKey } from 'cadenza/workbook/model/common-view-settings-key.gen';
import { getViewById, getViewIdsByWorksheetId, isIntegrated } from 'cadenza/workbook/workbook-core-selectors.state';
import { getSetting } from 'cadenza/workbook/workbook-settings-util';
import { compareViewByRowAndColumn } from 'cadenza/workbook/workbook-utils';
import { isDashboardLayout } from 'cadenza/workbook/worksheet-layout';

// Note: Exporting the names from the individual panel's module
// would lead to cyclic module dependencies.
export const WorkbookPanelName = {
  ANALYSIS_CONTEXT: 'd-analysis-context-panel',
  VIEW_SETTINGS: 'd-view-settings-panel', // Currently called designer
  DATA_BROWSER: 'd-data-browser-panel',
} as const;

export type WorkbookPanelName = (typeof WorkbookPanelName)[keyof typeof WorkbookPanelName];

const DISPLAY_STATE_PARAM = 'restoreDisplayState';

/**
 * Moves panel visibility settings from the optional "panels" URL param
 * to sessionStorage and removes the param from the URL.
 */
export function initializePanelVisibility(panelsParameter: string) {
  // hide all sidepanels on small displays
  if (!isDashboardLayout()) {
    Object.values(WorkbookPanelName).forEach((panelName) => {
      cadenzaSessionStorage.setItem(panelName, 'false');
    });
  }
  const url = new URL(location.href);
  let panelsParam = url.searchParams.get(panelsParameter);
  if (panelsParam != null) {
    const panelsToShow = panelsParam.split(',');
    Object.values(WorkbookPanelName).forEach((panelName) => {
      cadenzaSessionStorage.setItem(panelName, String(panelsToShow.includes(panelName)));
    });
    url.searchParams.delete(panelsParameter);
    history.replaceState(null, '', url.toString());
  }
}

export function getPanelVisibilityFromDisplayState(displayState?: DisplayState) {
  const url = new URL(location.href);
  if (displayState && isDashboardLayout() && url.searchParams.get(DISPLAY_STATE_PARAM) === 'true') {
    url.searchParams.delete(DISPLAY_STATE_PARAM);
    history.replaceState(null, '', url);
    return {
      [WorkbookPanelName.ANALYSIS_CONTEXT]: displayState.isAnalysisContextDisplayed,
      [WorkbookPanelName.VIEW_SETTINGS]: displayState.isDesignerDisplayed,
      [WorkbookPanelName.DATA_BROWSER]: displayState.isDataBrowserDisplayed,
    };
  }
}

function getWorksheetViews(worksheetId: WorksheetId) {
  const viewIds = getViewIdsByWorksheetId(getStore().getState(), worksheetId);
  return viewIds.map((viewId) => getViewById(getStore().getState(), viewId)).filter((view) => !isIntegrated(view));
}

export function getSortedViews(autoenabled: boolean, worksheetId: WorksheetId) {
  const views = getWorksheetViews(worksheetId);
  return autoenabled ? views.sort(compareViewByRowAndColumn) : views.sort(sortViewByOrder);
}

function sortViewByOrder(viewA: ViewDto, viewB: ViewDto) {
  const viewAOrder = getSetting<number>(viewA, LINEAR_LAYOUT_ORDER_KEY);
  const viewBOrder = getSetting<number>(viewB, LINEAR_LAYOUT_ORDER_KEY);

  if (viewAOrder === undefined || viewBOrder === undefined) {
    return 0;
  }

  return viewAOrder - viewBOrder;
}

export const LINEAR_LAYOUT_ORDER_KEY = CommonViewSettingsKey.toPersistenceKey('LINEAR_LAYOUT_ORDER');
